import { ethers, Contract, JsonRpcProvider } from "ethers";
import { Network } from "./types";
import { getRpcUrl } from "./config";
import { ContractError } from "./errors";

// Generic ERC-20 surface — nothing token-specific. Any address the payments contract's registry
// accepts can be read and moved through here.
export const ERC20_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address account) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
  // symbol() so a caller can label an amount without hardcoding a ticker.
  "function symbol() view returns (string)",
  // The event fragment is REQUIRED for contract.filters.Transfer(...). Without it ethers throws
  // "c.filters.Transfer is not a function", which broke transfer-history reads for every token.
  "event Transfer(address indexed from, address indexed to, uint256 value)",
];

/**
 * The token's own decimals. THROWS rather than assuming 18: this scales every amount, and a
 * 6-decimal token read as 18 misreports by 10^12 — a plausible-looking wrong number is worse than
 * a visible failure.
 */
export async function getTokenDecimals(token: Contract): Promise<number> {
  const raw = await token.decimals();
  const decimals = Number(raw);
  if (!Number.isFinite(decimals) || decimals < 0 || decimals > 36) {
    throw new Error(`token reported implausible decimals: ${String(raw)}`);
  }
  return decimals;
}

/**
 * ERC-20 balance of `address` in `tokenAddress`, scaled by the token's own decimals.
 */
export async function getTokenBalanceOf(
  address: string,
  network: Network,
  tokenAddress: string,
): Promise<number> {
  const provider = new JsonRpcProvider(getRpcUrl(network));
  const token = new Contract(tokenAddress, ERC20_ABI, provider);
  const decimals = await getTokenDecimals(token);
  const raw = await token.balanceOf(address);
  return parseFloat(ethers.formatUnits(raw, decimals));
}

export async function sendToken(
  token: Contract,
  provider: ethers.Provider,
  senderAddress: string,
  toAddress: string,
  humanAmount: string,
  onProgress?: (msg: string) => void,
): Promise<ethers.TransactionResponse> {
  // Read, never assume. This is a SEND path: an amount scaled by 18 for a 6-decimal token
  // transfers 10^12 times what the operator typed.
  let decimals: number;
  try {
    decimals = Number(await token.decimals());
    if (!Number.isFinite(decimals)) throw new Error("non-numeric decimals");
  } catch (err: any) {
    throw new ContractError(
      "INSUFFICIENT_TOKEN_BALANCE",
      `Could not read the token's decimals (${err?.message ?? err}). Refusing to send rather ` +
        `than guess the amount.`,
    );
  }

  const amountBN = ethers.parseUnits(humanAmount, decimals);

  const [maticBalance, tokenBalance] = await Promise.all([
    provider.getBalance(senderAddress),
    token.balanceOf(senderAddress),
  ]);

  if (maticBalance === BigInt(0)) {
    throw new ContractError(
      "INSUFFICIENT_GAS",
      "Insufficient MATIC for gas fees. Fund your address with MATIC before sending.",
    );
  }

  if (tokenBalance < amountBN) {
    const human = parseFloat(ethers.formatUnits(tokenBalance, decimals));
    throw new ContractError(
      "INSUFFICIENT_TOKEN_BALANCE",
      `Insufficient balance. Available: ${human}`,
    );
  }

  onProgress?.("Broadcasting transaction…");
  return token.transfer(toAddress, amountBN);
}
